import { SnackbarProvider, useSnackbar, VariantType } from 'notistack';
import React, { ReactElement, useRef } from 'react';
import classes from './AppSnackbarProvider.module.scss';
import { WMButton, WMButtonVariant } from '@walkme/wm-ui';
import { removeSnackbar, closeSnackbar as closeSnackbarAction } from '../../state/actions/ui';
import { useDispatch } from 'react-redux';
import useSelector from '../../infrastructure/useSelector';
import { ReactComponent as UndoIcon } from '../../../assets/icons/undo.svg';
import { ReactComponent as XIcon } from '../../../assets/icons/x-icon.svg';
import { ReactComponent as SnackbarSuccessIcon } from '../../../assets/icons/snackbarSuccess.svg';
import { ReactComponent as SnackbarErrorIcon } from '../../../assets/icons/snackbarError.svg';
import { useTranslation } from 'react-i18next';

const keyPrefix = 'components.appSnackbarProvider';

let displayed: $TSFixMe = [];

export enum SNACKBAR_ACTION_TYPES {
    undo = 'undo'
}

export interface ISnackbarNotifications {
    key?: string;
    message: string | ReactElement;
    action?: {
        callback: () => $TSFixMe;
        type?: SNACKBAR_ACTION_TYPES;
        text?: string;
    };
    options: { key?: string; variant: VariantType };
    dismissed?: boolean;
}

const Notifier = () => {
    const { t } = useTranslation('', { keyPrefix });
    const dispatch = useDispatch();
    const notifications = useSelector(store => store.ui.snackbarNotifications);
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();

    const storeDisplayed = (id: $TSFixMe) => {
        displayed = [...displayed, id];
    };

    const removeDisplayed = (id: $TSFixMe) => {
        // @ts-expect-error ts-migrate(7006) FIXME: Parameter 'key' implicitly has an 'any' type.
        displayed = [...displayed.filter(key => id !== key)];
    };

    React.useEffect(() => {
        notifications.forEach(({ key, message, action, options, dismissed = false }: ISnackbarNotifications) => {
            if (dismissed) {
                closeSnackbar(key);
                return;
            }

            if (displayed.includes(key)) return;
            let actionTriggered = false;

            enqueueSnackbar(message, {
                key,
                action: key => {
                    const onClose = () => {
                        dispatch(closeSnackbarAction(key));
                    };

                    const onClickAction = () => {
                        dispatch(closeSnackbarAction(key));
                        actionTriggered = true;
                    };

                    return (
                        <>
                            {action ? (
                                <WMButton
                                    className={classes.undo}
                                    variant={WMButtonVariant.Text}
                                    onClick={onClickAction}
                                    data-testid={`snackbar-undo-${key}`}
                                >
                                    {action.type === SNACKBAR_ACTION_TYPES.undo && <UndoIcon className={classes.undoIcon} />}
                                    {action.text || t('undo')}
                                </WMButton>
                            ) : null}

                            <div className={classes.labelClose} onClick={onClose} data-testid={`close-snackbar-${key}`}>
                                <XIcon fontSize={12} />
                            </div>
                        </>
                    );
                },
                ...options,
                onExited: (event, key) => {
                    if (actionTriggered && action?.callback) {
                        action.callback();
                    }
                    dispatch(removeSnackbar(key));
                    removeDisplayed(key);
                }
            });

            storeDisplayed(key);
        });
    }, [notifications, closeSnackbar, enqueueSnackbar, dispatch, t]);

    return null;
};

const AppSnackbarProvider = ({ children }: $TSFixMe) => {
    const notistackRef = useRef<$TSFixMe>();

    return (
        <SnackbarProvider
            iconVariant={{ success: <SnackbarSuccessIcon className={classes.icon} />, error: <SnackbarErrorIcon className={classes.icon} /> }}
            className={classes.snackbar}
            ref={notistackRef}
            maxSnack={3}
            classes={{ variantError: classes.error, variantSuccess: classes.success }}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
            <Notifier />

            {children}
        </SnackbarProvider>
    );
};

export default AppSnackbarProvider;
